import React from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Typography, Divider, Grid } from "@mui/material";
import { TnADayDataRef } from "../../../../models/tna/tna-data/tna-day-data-ref";
import SimplePopover from "../../../../reusable-components/SimplePopover/SimplePopover";
import { HOW_TO_REPORT_TIME, TNA_CLOCKING_TYPES } from "../../../../utils/constants/tna-constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faUserCheck,
  faUserXmark,
  faUserClock,
  faCircleCheck,
  faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import ClockingsList from "../../../MyClockings/ClockingsList/ClockingsList";
import { TnAConfigRef } from "../../../../models/tna";
import { fixHourFormat } from "../../../../utils/fix-hour-format";
import { StatusIndicator } from "../../../MyCalendar/DetailedFullCalendar/styles";

interface Props {
  dayData: TnADayDataRef;
  tnaConfig: TnAConfigRef;
}
const DayDataSummary = ({ dayData, tnaConfig }: Props) => {
  const theme = useTheme();
  const tRP = "components.custom_widget_links.my_presence_and_leaves.my_timesheet";
  const { t } = useTranslation();
  return (
    <>
      <SimplePopover
        buttonType="icon"
        buttonContent={
          <FontAwesomeIcon
            style={{ color: theme.palette.primary.main }}
            icon={faCircleInfo}
          />
        }
      >
        <Grid
          style={{
            padding: "10px",
            width: "fit-content",
            display: "flex",
            flexDirection: "column",
            gap: "15px",
          }}
        >
          <Grid
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <Typography style={{ color: theme.palette.text.secondary }}>Today's schedule :</Typography>
            <Typography style={{ color: theme.palette.text.primary }}>
              <b>{tnaConfig?.schedule?.code}</b>
            </Typography>

            {Number(tnaConfig?.schedule?.intervals.filter((interval) => interval)?.length) > 0 ? (
              tnaConfig?.schedule?.intervals
                .filter((interval) => interval.weekDay === new Date().getDay())
                .map((interval) => (
                  <Typography key={interval.id}>
                    {fixHourFormat(interval.startHour)}:{fixHourFormat(interval.startMinute)} -{" "}
                    {fixHourFormat(interval.endHour)}:{fixHourFormat(interval.endMinute)}
                  </Typography>
                ))
            ) : (
              <>{`${t(`${tRP}.no_interval`)}`} </>
            )}
          </Grid>

          <Divider orientation="horizontal" />
          {tnaConfig.how2ReportTime?.id === HOW_TO_REPORT_TIME.ClockingManually.id ? (
            <ClockingsList
              title={"Today's clockings"}
              list={dayData.clockings}
            />
          ) : tnaConfig.how2ReportTime?.id === HOW_TO_REPORT_TIME.ManualWorkedHours.id ? (
            <Grid>
              <Typography style={{ color: theme.palette.text.secondary }}>Today's worked hours : </Typography>
              {dayData.workedHours?.length > 0 ? (
                dayData.workedHours?.map((workedHour) => (
                  <Grid>
                    {workedHour.amount.toFixed(2)} h ({workedHour.client?.name} / {workedHour.project?.name})
                  </Grid>
                ))
              ) : (
                <>None</>
              )}
            </Grid>
          ) : tnaConfig.how2ReportTime?.id === HOW_TO_REPORT_TIME.ManualWorkedDays.id ? (
            <Grid style={{ display: "flex", gap: "3px", alignItems: "center" }}>
              <Typography style={{ color: theme.palette.text.disabled }}>Is reported as worked day : </Typography>
              {dayData.workedDayReported ? (
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  style={{ color: theme.palette.success.main }}
                  title={"Reported as worked day"}
                />
              ) : (
                <FontAwesomeIcon
                  icon={faCircleXmark}
                  style={{ color: theme.palette.error.main }}
                  title={"Not reported as worked day"}
                />
              )}
            </Grid>
          ) : (
            <></>
          )}

          <Divider orientation="horizontal" />
          <Grid>
            <Typography style={{ color: theme.palette.text.secondary }}>Today's Absences :</Typography>{" "}
            {dayData.absences.length === 0 ? (
              <>None</>
            ) : (
              <Grid>
                {dayData.absences.map((absence, idx) => (
                  <StatusIndicator
                    isRejected={absence.isRejected}
                    isCancelled={absence.isCancelled}
                    isApproved={absence.isApproved}
                    key={absence.id}
                  >
                    <Typography>
                      {absence.absenceType?.code}{" "}
                      {!absence.isRejected && !absence.isCancelled && !absence.isApproved ? " ?" : ""}
                    </Typography>
                    {absence.needAcceptance &&
                      (absence.acceptedByClient ? (
                        <FontAwesomeIcon
                          icon={faUserCheck}
                          color={"greenyellow"}
                          title={`Accepted by client ${
                            absence.acceptedRejectedByUser?.name ?? absence.acceptedRejectedByEmail
                          }`}
                        />
                      ) : absence.rejectedByClient ? (
                        <FontAwesomeIcon
                          icon={faUserXmark}
                          color={theme.palette.error.main}
                          title={`Rejected by client ${
                            absence.acceptedRejectedByUser?.name ?? absence.acceptedRejectedByEmail
                          }`}
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={faUserClock}
                          color={theme.palette.text.secondary}
                          title={"Pending client acceptance"}
                        />
                      ))}
                  </StatusIndicator>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </SimplePopover>
    </>
  );
};
export default DayDataSummary;
